// frontend/src/widgets/MetricGaugeWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-04
// Updated:      2026-09-08 16:10 BST
//
// Description:
//   Clean circular gauge with 8px ring and formatted value for system metrics.
//
// Lifecycle Status:
//   Active.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing circular gauge presentation.
//   - Uses a slightly smaller gauge footprint below 768px while retaining the
//     original 110px desktop size.
//   - Reduces mobile padding so multiple gauges fit comfortably in narrow layouts.
//   - Allows metric labels to wrap safely without horizontal overflow.
//   - Uses one render path for desktop and mobile.
//
// Data Safety (2026-09-08):
//   - Normalises non-numeric values to zero.
//   - Clamps the rendered gauge value between zero and max so malformed metric
//     payloads cannot overdraw the circular progress indicator.
//   - Preserves the original numeric display formatting and warning thresholds.

import React from "react";

import {
  CircularProgressbar,
  buildStyles,
} from "react-circular-progressbar";

import "react-circular-progressbar/dist/styles.css";

const MetricGaugeWidget = ({
  label = "Metric",
  value = 0,
  unit = "%",
  warning = 70,
  critical = 90,
  max = 100,
}) => {
  const numericValue =
    Number.parseFloat(
      value
    );

  const numericMax =
    Number.parseFloat(
      max
    );

  const safeMax =
    Number.isFinite(
      numericMax
    ) &&
    numericMax > 0
      ? numericMax
      : 100;

  const safeValue =
    Number.isFinite(
      numericValue
    )
      ? Math.min(
          safeMax,
          Math.max(
            0,
            numericValue
          )
        )
      : 0;

  const getColor = (val) => {
    if (
      val >= critical
    ) {
      return "#e74c3c";
    }

    if (
      val >= warning
    ) {
      return "#f1c40f";
    }

    return "#2ecc71";
  };

  const color =
    getColor(
      safeValue
    );

  const displayText =
    Number.isFinite(
      numericValue
    )
      ? `${safeValue.toFixed(
          1
        )}${unit}`
      : `--${unit}`;

  return (
    <div className="flex min-w-0 flex-col items-center justify-center p-3 md:p-4">
      <div className="h-[104px] w-[104px] shrink-0 md:h-[110px] md:w-[110px]">
        <CircularProgressbar
          value={
            safeValue
          }
          maxValue={
            safeMax
          }
          text={
            displayText
          }
          strokeWidth={8}
          styles={buildStyles({
            pathColor:
              color,
            trailColor:
              "#ddd",
            textColor:
              "#222",
            textSize:
              "14px",
            strokeLinecap:
              "round",
            pathTransitionDuration:
              0.3,
          })}
        />
      </div>

      <div
        className="mt-2 max-w-[140px] break-words text-center text-sm font-medium leading-5 text-gray-800"
        style={{
          overflowWrap:
            "anywhere",
        }}
      >
        {label}
      </div>
    </div>
  );
};

export default MetricGaugeWidget;
